import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, RefreshCw, Loader2, Zap, ShieldCheck, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : null);

export const SubscriptionStatusCard = () => {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async (manual = false) => {
    if (manual) setChecking(true);
    try {
      const res = await axios.get(`${API}/payments/my-subscription-status`, { withCredentials: true });
      setStatus(res.data);
      if (res.data.repaired) {
        toast.success(res.data.plan === 'premium' ? 'Votre abonnement Premium vient d\'être activé sur votre tableau de bord' : 'Votre abonnement a été mis à jour');
        refreshUser?.();
      } else if (manual) {
        toast.success('Vérification terminée : votre tableau de bord correspond bien à votre abonnement');
      }
    } catch {
      if (manual) toast.error('Vérification impossible pour le moment');
    } finally {
      setChecking(false);
    }
  }, [refreshUser]);

  useEffect(() => { check(false); }, [check]);
  useEffect(() => {
    const onUpdated = () => check(false);
    window.addEventListener('bc-subscription-updated', onUpdated);
    return () => window.removeEventListener('bc-subscription-updated', onUpdated);
  }, [check]);

  const premium = status?.plan === 'premium';

  return (
    <Card className={`card-surface ${premium ? 'border-[#F59E0B]/40' : ''}`} data-testid="subscription-status-card">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${premium ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-[#1E3A8A]/10 text-[#1E3A8A]'}`}>
              {premium ? <Zap className="w-6 h-6" /> : <ShieldCheck className="w-6 h-6" />}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xl font-heading font-bold text-[#111827]" data-testid="subscription-status-plan">{status ? (premium ? 'Abonnement Premium' : 'Plan Gratuit') : 'Abonnement'}</p>
                {status && (
                  <Badge className={premium ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'} data-testid="subscription-status-badge">
                    {premium ? 'Actif — tout est opérationnel' : status.status === 'expired' ? 'Expiré' : 'Gratuit'}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-[#4B5563] mt-1">
                {premium && status?.expiry && `Valable jusqu'au ${fmtDate(status.expiry)} · `}
                {premium && status?.source === 'admin' && 'Accordé par BuildConnect · '}
                {status?.last_payment?.paid_at && `Dernier paiement le ${fmtDate(status.last_payment.paid_at)} · `}
                <span className="inline-flex items-center gap-1 text-[#6B7280]"><Clock className="w-3.5 h-3.5" /> Vérifié par l'agent à {fmtTime(status?.verified_at)}</span>
              </p>
              <p className="text-xs text-[#6B7280] mt-2" data-testid="subscription-status-delay">
                Tout changement d'abonnement (gratuit → Premium ou inversement) est appliqué automatiquement à votre tableau de bord dans un délai <strong>maximum de {status?.sync_interval_minutes ?? 15} minutes</strong> ; un agent vérifie que toutes les fonctionnalités sont bien actives.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => check(true)} disabled={checking} data-testid="subscription-verify-now">
              {checking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Vérifier maintenant
            </Button>
            {!premium && (
              <Link to="/dashboard/subscription"><Button size="sm" className="btn-accent" data-testid="subscription-upgrade-link"><Zap className="w-4 h-4 mr-1" /> Passer Premium</Button></Link>
            )}
          </div>
        </div>
        {status && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-5" data-testid="subscription-features">
            {status.features.map((f) => (
              <div key={f.key} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md ${f.active ? 'bg-emerald-50 text-emerald-800' : 'bg-[#F3F4F6] text-[#9CA3AF]'}`} data-testid={`feature-${f.key}`}>
                {f.active ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
                <span>{f.label}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
